import {
  DocumentProperties,
  DocumentUploadUrl,
  DocumentUploadUrlResponse,
  EmailDocumentCommand,
  RelatedDocument,
  ShippingLabelFormat,
  SignedDocument,
} from '@/model';
import { decodeBinaryErrorResponse } from '@/util/RestErrorUtil';

import { BaseHttpController } from './BaseHttpController';

const endpoint = 'shipment';

export class ShipmentDocumentController extends BaseHttpController {
  constructor() {
    super(endpoint);
  }

  async getDocumentsByShipmentId(
    shipmentId: number,
  ): Promise<SignedDocument[]> {
    const response = await this.http.get<SignedDocument[]>(
      `${shipmentId}/document`,
    );
    return response.data.map((val) => new SignedDocument(val));
  }

  async getRelatedDocumentsByShipmentId(
    shipmentId: number,
  ): Promise<RelatedDocument[]> {
    const response = await this.http.get<RelatedDocument[]>(
      `${shipmentId}/document/related`,
    );
    return response.data.map((val) => new RelatedDocument(val));
  }

  async getUploadUrl(
    shipmentId: number,
    documentUploadUrl: DocumentUploadUrl,
  ): Promise<DocumentUploadUrlResponse> {
    const response = await this.http.post<DocumentUploadUrlResponse>(
      `${shipmentId}/document/upload-url`,
      documentUploadUrl,
    );
    return new DocumentUploadUrlResponse(response.data);
  }

  async attachDocument(
    shipmentId: number,
    documentProperties: DocumentProperties,
  ): Promise<SignedDocument> {
    const response = await this.http.post<SignedDocument>(
      `${shipmentId}/document`,
      documentProperties,
    );
    return new SignedDocument(response.data);
  }

  async updateDocument(
    shipmentId: number,
    documentId: number,
    documentProperties: DocumentProperties,
  ): Promise<SignedDocument> {
    const response = await this.http.put<SignedDocument>(
      `${shipmentId}/document/${documentId}`,
      documentProperties,
    );

    return new SignedDocument(response.data);
  }

  async removeDocument(shipmentId: number, documentId: number): Promise<void> {
    await this.http.delete<void>(`${shipmentId}/document/${documentId}`);
  }

  async generateBol(shipmentId: number, templateId?: number): Promise<Blob> {
    const response = await this.http.get<Blob>(`${shipmentId}/document/bol`, {
      responseType: 'arraybuffer',
      params: { templateId },
    });

    const contentType = response.headers['content-type'];
    return new Blob([response.data], { type: contentType });
  }

  async generateBolWithTotalHandlingUnitOverride(
    shipmentId: number,
    totalHandlingUnitOverride: number,
    templateId?: number,
  ): Promise<Blob> {
    const response = await this.http.post<Blob>(
      `${shipmentId}/document/bol/total-handling-unit-override`,
      { totalHandlingUnitOverride, templateId },
      { responseType: 'arraybuffer' },
    );

    const contentType = response.headers['content-type'];
    return new Blob([response.data], { type: contentType });
  }

  async emailDocuments(
    shipmentId: number,
    emailDocumentCommand: EmailDocumentCommand,
  ): Promise<void> {
    await this.http.post<void>(
      `${shipmentId}/document/email-documents`,
      emailDocumentCommand,
    );
  }

  async resendConfirmation(shipmentId: number, emails: string): Promise<void> {
    await this.http.post(`${shipmentId}/document/resend-confirmation`, {
      emails: emails.split(',').map((email) => email.trim()),
    });
  }

  async generateShippingLabel(
    shipmentId: number,
    count: string,
    templateId?: number,
    format: ShippingLabelFormat = ShippingLabelFormat.PDF,
  ): Promise<Blob> {
    const response = await this.http
      .get<Blob>(`${shipmentId}/document/shipping-label/${count}`, {
        responseType: 'arraybuffer',
        // Only send format for ZPL so PDF requests stay identical to before the flag.
        params: {
          templateId,
          format: format === ShippingLabelFormat.ZPL ? format : undefined,
        },
      })
      .catch(async (e: unknown) => {
        // Surface the JSON validation error (e.g. ZPL for a non-4x6 template) instead of raw bytes.
        await decodeBinaryErrorResponse(e);
        throw e;
      });

    const contentType = response.headers['content-type'];
    return new Blob([response.data], { type: contentType });
  }

  /** Converts a shipping label document already attached to the shipment to ZPL. */
  async convertAttachedShippingLabelToZpl(
    shipmentId: number,
    documentId: number,
  ): Promise<Blob> {
    const response = await this.http
      .get<Blob>(`${shipmentId}/document/${documentId}/shipping-label/zpl`, {
        responseType: 'arraybuffer',
      })
      .catch(async (e: unknown) => {
        await decodeBinaryErrorResponse(e);
        throw e;
      });

    const contentType = response.headers['content-type'];
    return new Blob([response.data], { type: contentType });
  }
}
